import { useNavigate } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { useDrivers } from '@/hooks/useDrivers'
import { useUrlState } from '@/hooks/useUrlState'
import { DataTable } from '@/components/ui/data-table'
import { SearchInput } from '@/components/ui/search-input'
import { Pagination } from '@/components/ui/pagination'
import { StatusBadge } from '@/components/ui/status-badge'
import { ExpiryBadge } from '@/components/ui/expiry-badge'
import { Button } from '@/components/ui/button'
import { maskCpf, maskPhone } from '@/components/form/masks'
import type { Driver } from '@/types/driver'
import type { Column } from '@/components/ui/data-table'

const DRIVER_TYPE_LABEL: Record<string, string> = {
  OWN: 'Próprio',
  AGGREGATE: 'Agregado',
}

type ActiveFilter = 'all' | 'active' | 'inactive'

const FILTER_LABELS: Record<ActiveFilter, string> = {
  all: 'Todos',
  active: 'Ativos',
  inactive: 'Inativos',
}

const COLUMNS: Column<Driver>[] = [
  {
    key: 'name',
    header: 'Nome',
    render: (row) => (
      <span className="inline-flex items-center gap-2">
        {row.name}
        <ExpiryBadge date={row.cnhExpiry} label="CNH" />
      </span>
    ),
  },
  {
    key: 'cpf',
    header: 'CPF',
    render: (row) => maskCpf(row.cpf),
  },
  {
    key: 'driverType',
    header: 'Vínculo',
    render: (row) => DRIVER_TYPE_LABEL[row.driverType] ?? row.driverType,
  },
  {
    key: 'phone',
    header: 'Telefone',
    className: 'hidden lg:table-cell',
    render: (row) => (row.phone ? maskPhone(row.phone) : '—'),
  },
  {
    key: 'city',
    header: 'Cidade/UF',
    className: 'hidden lg:table-cell',
    render: (row) =>
      row.city && row.state ? `${row.city} / ${row.state}` : row.city ?? '—',
  },
  {
    key: 'active',
    header: 'Status',
    render: (row) => <StatusBadge active={row.active} />,
  },
]

export function DriverListPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useUrlState<string>('search', '')
  const [activeFilter, setActiveFilter] = useUrlState<ActiveFilter>('active', 'all')
  const [page, setPage] = useUrlState<number>('page', 1)

  const activeParam =
    activeFilter === 'active' ? true : activeFilter === 'inactive' ? false : undefined

  const { data, isLoading } = useDrivers({ search, active: activeParam, page })

  function handleSearch(v: string) {
    setSearch(v)
    setPage(1)
  }

  function handleFilter(f: ActiveFilter) {
    setActiveFilter(f)
    setPage(1)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Motoristas</h1>
          <p className="text-sm text-muted-foreground">Gerenciamento de motoristas</p>
        </div>
        <Button onClick={() => navigate('/drivers/new')}>
          <Plus size={16} />
          Novo motorista
        </Button>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <SearchInput
          value={search}
          onChange={handleSearch}
          placeholder="Buscar por nome ou CPF…"
          className="flex-1"
        />
        <div className="flex overflow-hidden rounded-md border border-input text-sm">
          {(Object.keys(FILTER_LABELS) as ActiveFilter[]).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => handleFilter(f)}
              className={`px-3 py-1.5 transition-colors ${
                activeFilter === f
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted/50'
              }`}
            >
              {FILTER_LABELS[f]}
            </button>
          ))}
        </div>
      </div>

      <DataTable
        columns={COLUMNS}
        data={data?.data ?? []}
        keyExtractor={(row) => row.id}
        onRowClick={(row) => navigate(`/drivers/${row.id}`)}
        isLoading={isLoading}
        emptyTitle="Nenhum motorista encontrado"
        emptyDescription="Comece cadastrando o primeiro motorista."
        emptyAction={
          <Button onClick={() => navigate('/drivers/new')}>
            <Plus size={16} />
            Novo motorista
          </Button>
        }
      />

      {data && (
        <Pagination
          page={data.page}
          totalPages={data.totalPages}
          total={data.total}
          perPage={data.perPage}
          onChange={setPage}
        />
      )}
    </div>
  )
}
